import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  Row, Modal, ModalHeader, ModalBody, ModalFooter
} from 'reactstrap'

import ConsignorSettlementsApiV2 from 'api/v2/admins/consignor-settlements-api-v2'
import { singleSaveBlockers } from '../utils/single-save-blockers'
import TitlePage from 'components/atoms/title-page'
import SettlementConnectUser from '../components/settlement-connect-user'
import SettlementEmailDeliveryReconciliation from '../components/settlement-email-delivery-reconciliation'
import { financeSubmissionStage, transferCompletionNotice, settlementStatusNote, settlementEmailLabel } from '../utils/finance-submission-guidance'
import SettlementPaymentLedger, { SettlementMovement, PaidTransfersSummary } from '../components/settlement-payment-ledger'

const propTypes = {
  transferDetail: PropTypes.bool,
  canFinanceTransfer: PropTypes.bool,
  canNegotiate      : PropTypes.bool,
  listPath          : PropTypes.string.isRequired,
  settlementKind    : PropTypes.oneOf([ 'consignor', 'seller', 'all' ]),
  teamLabel         : PropTypes.string
}

const defaultProps = {
  canFinanceTransfer : false,
  canNegotiate       : false,
  settlementKind     : 'consignor',
  teamLabel          : ''
}


const LEGACY_STATE_LABELS = {
  transferred_manually: 'Transfer Done Manually',
  draft                 : 'Draft',
  ready_for_negotiation : 'On Process',
  negotiated                  : 'On Process',
  waiting_finance_confirmation : 'Finance Approval',
  finance_requested           : 'Ready to Transfer',
  transferred           : 'Transfer Done',
  email_failed          : 'Email Failed',
  email_sent            : 'Email Delivered',
  cancelled             : 'Cancelled'
}

const STATE_LABELS = {
  transferred_manually: 'Transfer Done Manually',
  draft                        : 'On Process',
  ready_for_negotiation        : 'On Process',
  negotiated                   : 'On Process',
  waiting_finance_confirmation : 'Finance Approval',
  finance_requested            : 'Ready to Transfer',
  transferred                  : 'Transfer Done',
  email_failed                 : 'Email Failed',
  email_sent                   : 'Email Delivered',
  cancelled                    : 'Cancelled'
}

const EMAIL_STATUS_LABELS = {
  not_mandatory_sent: 'Tidak wajib kirim (detail Seller)',
  not_sent  : 'Not Sent',
  queued    : 'Queued',
  submitted : 'Submitted',
  processed : 'Processed',
  deferred  : 'Deferred',
  delivered : 'Delivered',
  failed    : 'Failed'
}

const FINANCE_CONFIRMATION_LABELS = {
  not_requested : 'Not Requested',
  pending       : 'Finance Approval',
  confirmed     : 'Approved',
  rejected      : 'Correction / Re-Negotiation Required'
}

const LEGACY_FINANCE_CONFIRMATION_LABELS = {
  not_requested : 'Not Requested',
  pending       : 'Finance Approval',
  confirmed     : 'Approved',
  rejected      : 'Correction / Re-Negotiation Required'
}

const stateColor = (state) => {
  if (state === 'transferred_manually') return 'success'
  if (state === 'email_sent') return 'success'
  if (state === 'email_failed') return 'danger'
  if (state === 'transferred') return 'info'
  if (state === 'waiting_finance_confirmation') return 'warning'
  if (state === 'finance_requested') return 'primary'
  if (state === 'negotiated') return 'warning'
  if (state === 'cancelled') return 'danger'

  return 'secondary'
}

const emailStatusColor = (status) => {
  if (status === 'delivered') return 'success'
  if (status === 'failed') return 'danger'
  if (status === 'deferred') return 'warning'
  if ([ 'queued', 'submitted', 'processed' ].includes(status)) return 'info'

  return 'secondary'
}

const formatMoney = (value) => {
  if (value === null || value === undefined || value === '') return '-'

  const number = Number(value)
  if (Number.isNaN(number)) return value

  return new Intl.NumberFormat('id-ID').format(number)
}

const normalizeMoneyValue = (value) => {
  if (value === null || value === undefined || value === '') return ''

  const number = Number(value)

  if (!Number.isNaN(number)) {
    return Math.max(Math.trunc(number), 0).toString()
  }

  return value
    .toString()
    .replace(/[^0-9]/g, '')
    .replace(/^0+(?=\d)/, '')
}

const sanitizeMoneyInput = (value) => (
  value
    .toString()
    .replace(/[^0-9]/g, '')
    .replace(/^0+(?=\d)/, '')
)

const formatMoneyInput = (value) => {
  if (!value) return ''

  const number = Number(value)
  if (Number.isNaN(number)) return value

  return new Intl.NumberFormat('id-ID', {
    maximumFractionDigits: 0
  }).format(number)
}

const formatDateTime = (value) => {
  if (!value) return '-'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return date.toLocaleString('id-ID', {
    timeZone: 'Asia/Jakarta'
  })
}

const formatBytes = (value) => {
  const bytes = Number(value || 0)
  if (!bytes) return '-'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const displayValue = (value) => (
  value === null || value === undefined || value === ''
    ? '-'
    : value
)

const errorMessage = (error, fallback) => (
  (error?.response?.status === 413 ? 'Upload ditolak server karena ukuran request terlalu besar. Maksimal 5 MB per file; kurangi jumlah file dalam satu upload.' : null) ||
  error?.response?.data?.messages?.base ||
  error?.response?.data?.message ||
  error?.message ||
  fallback
)

const InfoRow = ({ label, value, mono }) => (
  <Row className='py-2 border-bottom'>
    <Col md={ 5 } className='text-muted mb-1 mb-md-0'>
      { label }
    </Col>
    <Col
      md={ 7 }
      style={{
        overflowWrap : 'break-word',
        whiteSpace   : 'normal',
        wordBreak    : 'break-word'
      }}
    >
      { mono
        ? <code>{ displayValue(value) }</code>
        : displayValue(value) }
    </Col>
  </Row>
)

InfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  mono : PropTypes.bool
}

InfoRow.defaultProps = {
  value : '-',
  mono  : false
}

const ProofList = ({ emptyText, files, title, onDelete, deleting }) => (
  <div>
    <div className='font-weight-bold mb-2'>{ title }</div>

    { files.length === 0 ? (
      <div className='text-muted small'>{ emptyText }</div>
    ) : (
      <div className='d-flex flex-wrap' style={{ gap: '12px' }}>
        { files.map((proof, index) => {
          const contentType = (proof.content_type || '').toLowerCase()
          const filename = proof.filename || `Proof ${index + 1}`
          const isImage = contentType.startsWith('image/') || /\.(jpe?g|png|webp)$/i.test(filename)
          const isPdf = contentType === 'application/pdf' || /\.pdf$/i.test(filename)

          return (
            <div
              key={ proof.id || `${proof.filename}-${index}` }
              className='border rounded p-2 mb-2'
              style={{ width: isPdf ? 420 : 240, maxWidth: '100%' }}
            >
              { isImage && proof.url ? (
                <a href={ proof.url } target='_blank' rel='noopener noreferrer'>
                  <img
                    src={ proof.url }
                    alt={ filename }
                    style={{
                      width     : '100%',
                      height    : 220,
                      objectFit : 'contain',
                      display   : 'block'
                    }}
                  />
                </a>
              ) : isPdf && proof.url ? (
                <iframe
                  src={ proof.url }
                  title={ filename }
                  style={{ width: '100%', height: 300, border: 0 }}
                />
              ) : null }

              { onDelete && <Button type='button' color='danger' outline size='sm' disabled={ deleting } onClick={ () => onDelete(proof) }>Delete</Button> }
              <div className={ isImage || isPdf ? 'mt-2' : '' } style={{ minWidth: 0 }}>
                { proof.url ? (
                  <a href={ proof.url } target='_blank' rel='noopener noreferrer' style={{ overflowWrap: 'anywhere' }}>
                    { filename }
                  </a>
                ) : (
                  <span className='text-danger' style={{ overflowWrap: 'anywhere' }}>
                    { filename } — file tidak ditemukan di storage; hapus dan unggah ulang pada bagian asal.
                  </span>
                ) }
                <div className='text-muted small'>
                  { formatBytes(proof.byte_size) }
                  { proof.created_at ? ` • ${formatDateTime(proof.created_at)}` : '' }
                </div>
              </div>
            </div>
          )
        }) }
      </div>
    ) }
  </div>
)

ProofList.propTypes = {
  onDelete: PropTypes.func,
  deleting: PropTypes.bool,
  emptyText: PropTypes.string,
  files    : PropTypes.arrayOf(PropTypes.shape({})),
  title    : PropTypes.string.isRequired
}

ProofList.defaultProps = {
  emptyText : 'Belum ada bukti yang di-upload.',
  files     : []
}

const settlementListReturnTo = (settlementKind, safeReturnTo) => {
  if (settlementKind !== 'seller' || !/^\/app\/buying\/po-sourcing(?:\?|$)/.test(safeReturnTo)) return safeReturnTo

  const [path, query = ''] = safeReturnTo.split('?')
  const params = new URLSearchParams(query)
  params.set('tab', 'settlement')
  return `${path}?${params.toString()}`
}

const ConsignorSettlementDetailPage = ({
  canFinanceTransfer,
  transferDetail,
  canNegotiate,
  listPath,
  settlementKind,
  teamLabel
}) => {
  const history = useHistory()
  const location = useLocation()
  const { id } = useParams()

  const confirmedPicId = useRef(location.state?.settlementPicConfirmedId || '')
  const [ settlement, setSettlement ] = useState(null)
  const [ paymentAmount, setPaymentAmount ] = useState('')
  const [ settlementBuyingPrice, setSettlementBuyingPrice ] = useState('')
  const [ notes, setNotes ] = useState('')
  const [ financeConfirmationNote, setFinanceConfirmationNote ] = useState('')
  const [ emailRecipient, setEmailRecipient ] = useState('')
  const [ bankAccountReferenceId, setBankAccountReferenceId ] = useState('')
  const [ selectedBankOptionKey, setSelectedBankOptionKey ] = useState('')
  const [ bankName, setBankName ] = useState('')
  const [ bankAccountNumber, setBankAccountNumber ] = useState('')
  const [ bankAccountHolderName, setBankAccountHolderName ] = useState('')
  const [ bankBranch, setBankBranch ] = useState('')
  const [ supportingDocumentsOpen, setSupportingDocumentsOpen ] = useState(false)
  const [ showManualTransfer, setShowManualTransfer ] = useState(false)
  const [ negotiationFiles, setNegotiationFiles ] = useState([])
  const [ transferFiles, setTransferFiles ] = useState([])
  const [ negotiationInputKey, setNegotiationInputKey ] = useState(0)
  const [ transferInputKey, setTransferInputKey ] = useState(0)
  const [ isLoading, setIsLoading ] = useState(true)
  const [ busyAction, setBusyAction ] = useState('')
  const [ apiMessage, setApiMessage ] = useState('')
  const [ priceError, setPriceError ] = useState('')
  const [ successMessage, setSuccessMessage ] = useState('')
  const [ lastSuccessAction, setLastSuccessAction ] = useState('')

  const syncSettlement = (data) => {
    setSettlement(data)
    setSettlementBuyingPrice(
      normalizeMoneyValue(data?.settlement_kind === 'seller' && data?.negotiation_completed === false && !data?.final_price_locked ? null : data?.settlement_buying_price)
    )
    setNotes(data?.notes || '')
    setFinanceConfirmationNote(data?.finance_confirmation_note || '')
    setEmailRecipient(data?.email_recipient || '')
    setBankAccountReferenceId(data?.bank_account_reference_id ? String(data.bank_account_reference_id) : '')
    const matchingBankOption = (data?.bank_account_options || []).find((account) => (
      (data?.bank_account_reference_id && String(account.id || '') === String(data.bank_account_reference_id)) ||
      (
        String(account.bank_name || '') === String(data?.bank_name || '') &&
        String(account.account_number || '') === String(data?.bank_account_number || '') &&
        String(account.holder_name || '') === String(data?.bank_account_holder_name || '')
      )
    ))
    setSelectedBankOptionKey(matchingBankOption?.option_key || '')
    setBankName(data?.bank_name || '')
    setBankAccountNumber(data?.bank_account_number || '')
    setBankAccountHolderName(data?.bank_account_holder_name || '')
    setBankBranch(data?.bank_branch || '')
  }

  const loadDetail = async (preserveCurrent = false) => {
    if (!preserveCurrent) setIsLoading(true)
    setApiMessage('')

    try {
      const response = await (transferDetail ? ConsignorSettlementsApiV2.getTransferDetail(id) : ConsignorSettlementsApiV2.getDetail(id))
      const data = response.data?.data || null
      if (data?.pic_warning_required === true && confirmedPicId.current !== String(id)) {
        const pic = String((data.settlement_kind === 'seller' ? data.sourced_by : data.product_curator_pic) || '').trim()
        if (!window.confirm(`Apakah kamu yakin mau buka Settlement Detail PIC ${!pic || pic === '-' ? 'yang belum di-assign' : pic}? Buying price milik PIC lain tetap disembunyikan.`)) {
          history.replace(listReturnTo)
          return
        }
        confirmedPicId.current = String(id)
      }
      syncSettlement(data)
    }
    catch (error) {
      if (preserveCurrent) throw error
      setSettlement(null)
      setApiMessage(errorMessage(error, 'Detail settlement gagal dimuat.'))
    }
    finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    setShowManualTransfer(false)
    loadDetail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ id ])

  const returnTo = new URLSearchParams(location.search).get('return_to')
  const safeReturnTo = returnTo && returnTo.startsWith('/app/')
    ? returnTo
    : listPath

  const listReturnTo = settlementListReturnTo(settlementKind, safeReturnTo)

  const backToList = () => {
    history.push(listReturnTo)
  }

  const routeModuleTitle = settlementKind === 'seller'
    ? 'Seller Settlement'
    : settlementKind === 'all'
      ? 'Consignor & Seller Settlement'
      : 'Consignor Settlement'

  const runWorkflow = async (actionName, request, fallbackMessage) => {
    setBusyAction(actionName)
    setApiMessage('')
    setSuccessMessage('')
    setLastSuccessAction('')

    try {
      const response = await request()
      if (actionName === 'delete-negotiation-proof') setSettlement(response.data?.data || settlement)
      else syncSettlement(response.data?.data || settlement)
      setSuccessMessage(fallbackMessage || response.data?.message)
      setLastSuccessAction(actionName)
      return true
    }
    catch (error) {
      if (['save-negotiation', 'mark-negotiated', 'save-all-submit'].includes(actionName)) setPriceError(errorMessage(error, fallbackMessage))
      else setApiMessage(errorMessage(error, fallbackMessage))
      return false
    }
    finally {
      setBusyAction('')
    }
  }

  const selectBankAccountOption = (event) => {
    const value = event.target.value
    setSelectedBankOptionKey(value)
    if (!value) {
      setBankAccountReferenceId('')
      setBankName('')
      setBankAccountNumber('')
      setBankAccountHolderName('')
      setBankBranch('')
      return
    }

    const account = (settlement?.bank_account_options || []).find(
      (option) => String(option.option_key || option.id || '') === value
    )

    if (!account) return

    setBankAccountReferenceId(account.source === 'user_bank_account' && account.id ? String(account.id) : '')
    setBankName(account.bank_name || '')
    setBankAccountNumber(account.account_number || '')
    setBankAccountHolderName(account.holder_name || '')
    setBankBranch(account.branch || '')
  }

  // Legacy flow (now replaced by one atomic submission):
  // The API stores the draft and the attachments in separate actions.
  const removeNegotiationProof = async (proof) => {
    if (busyAction || !window.confirm(`Delete ${proof.filename}? Bukti dihapus dari form ini. Bukti pengajuan pembayaran sebelumnya tetap tersimpan.`)) return
    await runWorkflow('delete-negotiation-proof', () => ConsignorSettlementsApiV2.removeNegotiationProof(id, proof.id), 'Bukti negosiasi dihapus dari form. Bukti pembayaran sebelumnya tetap tersimpan.')
  }

  const chooseNegotiationFiles = (event) => {
    const files = Array.from(event.target.files || [])
    if (files.some((file) => file.size <= 0 || file.size > 5 * 1024 * 1024)) {
      event.target.value = ''
      setPriceError('Bukti negosiasi maksimal 5 MB per file. Pilih file yang lebih kecil.')
      return
    }
    if (files.length + (settlement.negotiation_proofs || []).length > 5) {
      event.target.value = ''
      setPriceError('Maksimal 5 bukti negosiasi, termasuk file yang sudah tersimpan.')
      return
    }
    setNegotiationFiles(files)
  }

  const saveAllAndSubmit = async () => {
    if (busyAction) return
    const success = await runWorkflow('save-all-submit', () => ConsignorSettlementsApiV2.saveAndSubmit(id, {
      submission_version: settlement.submission_version, settlement_buying_price: settlementBuyingPrice,
      notes, email_recipient: emailRecipient, bank_account_reference_id: bankAccountReferenceId, amount: paymentAmount
    }, negotiationFiles), 'Data tersimpan dan diajukan ke Finance Approval.')
    if (success) { setNegotiationFiles([]); setNegotiationInputKey((key) => key + 1) }
  }

  const confirmFinance = async () => {
    if (!window.confirm('Approve request ini menjadi Ready to Transfer?')) return

    await runWorkflow(
      'confirm-finance',
      () => ConsignorSettlementsApiV2.confirmFinance(settlement.id, financeConfirmationNote, settlement.selected_transfer_id),
      'Finance Approval selesai. Settlement menjadi Ready to Transfer.'
    )
  }

  const rejectFinanceConfirmation = async () => {
    if (!financeConfirmationNote.trim()) {
      setApiMessage('Catatan koreksi / negosiasi ulang wajib diisi.')
      return
    }

    if (!window.confirm(settlement?.settlement_kind === 'seller' ? 'Return this payment request for correction?' : 'Kembalikan settlement ini ke Siap Negosiasi?')) return

    await runWorkflow(
      'reject-finance-confirmation',
      () => ConsignorSettlementsApiV2.rejectFinanceConfirmation(settlement.id, financeConfirmationNote, settlement.selected_transfer_id),
      'Settlement dikembalikan untuk koreksi / negosiasi ulang.'
    )
  }

  const cancelApprovedFinance = async () => {
    if (!settlement?.selected_transfer_id || busyAction) return
    const note = window.prompt('Alasan Cancel Approval (wajib). Jelaskan perubahan harga atau alasan pengajuan ulang:')
    if (note === null) return
    if (!note.trim()) {
      setApiMessage('Alasan Cancel Approval wajib diisi.')
      return
    }
    if (!window.confirm('Cancel Approval untuk Transfer ID ini? Permintaan akan dikembalikan ke BT/PC untuk koreksi dan harus diajukan ulang. Belum ada uang yang boleh ditransfer.')) return

    await runWorkflow(
      'reject-approved-finance',
      () => ConsignorSettlementsApiV2.rejectApprovedFinance(settlement.id, note.trim(), settlement.selected_transfer_id),
      'Approval dibatalkan oleh Finance. Request kembali ke On Process untuk koreksi / pengajuan ulang.'
    )
  }

  const uploadTransferProofs = async () => {
    if (transferFiles.length === 0) {
      setApiMessage('Pilih minimal satu bukti transfer terlebih dahulu.')
      return
    }

    const success = await runWorkflow(
      'upload-transfer-proof',
      () => ConsignorSettlementsApiV2.uploadTransferProofs(id, transferFiles),
      'Bukti transfer berhasil di-upload.'
    )

    if (success) {
      setTransferFiles([])
      setTransferInputKey((value) => value + 1)
    }
  }

  const markTransferredManually = async () => {
    const note = window.prompt('Mark as transfer done manually hanya apabila Transfer / Settlement sudah dilakukan di luar sistem untuk Deal sebelum 15 Oktober 2026 pukul 00.00 WIB. Isi catatan / referensi transfer manual:')
    if (!note || !note.trim()) return
    if (!window.confirm('Konfirmasi settlement ini sudah lunas di luar sistem? Draft pembayaran yang belum diajukan akan dibatalkan.')) return
    await runWorkflow(
      'mark-transferred-manually',
      () => ConsignorSettlementsApiV2.markTransferredManually(settlement.id, note.trim()),
      'Transfer Done Manually tersimpan.'
    )
  }

  const markTransferred = async () => {
    if (!window.confirm('Konfirmasi bahwa Finance sudah mentransfer settlement ini?')) return

    await runWorkflow(
      'mark-transferred',
      () => ConsignorSettlementsApiV2.markTransferred(id),
      'Settlement berhasil ditandai Sudah Ditransfer.'
    )
  }

  const saveEmailRecipient = async () => {
    const recipient = emailRecipient.trim().toLowerCase()
    if (!recipient || !recipient.includes('@')) {
      setApiMessage('Isi email tujuan yang valid terlebih dahulu.')
      return
    }

    await runWorkflow(
      'save-email-recipient',
      () => ConsignorSettlementsApiV2.updateEmailRecipient(id, recipient),
      'Consignor recipient email berhasil disimpan.'
    )
  }

  const sendTransferEmail = async () => {
    const resend = Number(settlement?.email_send_attempts || 0) > 0
    const verb = resend ? 'Kirim ulang' : 'Kirim'

    const confirmationMessage = settlement?.email_test_mode
      ? `${verb} email TEST bukti transfer? Intended consignor: ${settlement?.email_recipient || '-'}; actual test recipient: ${settlement?.email_test_recipient || '-'}.`
      : `${verb} email bukti transfer ke ${settlement?.email_recipient || 'email consignor'}?`

    if (
      !window.confirm(confirmationMessage)
    ) {
      return
    }

    await runWorkflow(
      'send-email',
      () => ConsignorSettlementsApiV2.sendTransferEmail(id),
      'Email bukti transfer masuk antrean pengiriman.'
    )
  }

  if (isLoading) {
    return (
      <>
        <TitlePage
          mainTitle={ routeModuleTitle }
          subTitle='Settlement Detail'
        />
        <Card>
          <CardBody className='text-center text-muted py-5'>
            Loading settlement detail...
          </CardBody>
        </Card>
      </>
    )
  }

  if (!settlement) {
    return (
      <>
        <TitlePage
          mainTitle={ routeModuleTitle }
          subTitle='Settlement Detail'
        />

        <Alert color='warning'>
          { apiMessage || 'Settlement not found.' }
        </Alert>

        <Button
          color='secondary'
          outline
          onClick={ backToList }
        >
          Back to List
        </Button>
      </>
    )
  }

  const permissions = settlement.permissions || {}
  const actualKind = settlement.settlement_kind || settlementKind
  const sellerMode = actualKind === 'seller'
  const useBusinessLabels = true
  const stateLabel = (state) => (
    (useBusinessLabels ? STATE_LABELS : LEGACY_STATE_LABELS)[state] || state || '-'
  )
  const financeConfirmationLabel = (status) => (
    (useBusinessLabels ? FINANCE_CONFIRMATION_LABELS : LEGACY_FINANCE_CONFIRMATION_LABELS)[status] || status || '-'
  )
  const moduleTitle = sellerMode ? 'Seller Settlement' : settlementKind === 'all' ? 'Consignor & Seller Settlement' : 'Consignor Settlement'
  const counterpartyLabel = sellerMode ? 'Seller' : 'Consignor'
  const negotiationOwnerLabel = sellerMode ? 'Buying Team' : 'Product Curator'
  const financeConfirmationStatus = settlement.finance_confirmation_status || 'not_requested'
  const negotiationProofs = settlement.negotiation_proofs || []
  const identityVerificationProofs = settlement.identity_verification_proofs || []
  const transferProofs = settlement.transfer_proofs || []
  const bankAccountOptions = settlement.bank_account_options || []
  const counterpartyEmailOptions = settlement.counterparty_email_options || []
  const uniqueFinanceBlockers = singleSaveBlockers(settlement, {
    price: settlementBuyingPrice, email: emailRecipient, bankKey: selectedBankOptionKey,
    amount: paymentAmount, files: negotiationFiles
  })
  const requestFinanceReady = uniqueFinanceBlockers.length === 0
  const submissionStage = financeSubmissionStage(settlement)
  const showFinanceGuidance = canNegotiate && !canFinanceTransfer &&
    permissions.can_submit_finance_request && submissionStage === 'prepare'
  const showFinanceRequirements = showFinanceGuidance && !requestFinanceReady
  const completionNotice = transferCompletionNotice(settlement, sellerMode)
  const markTransferredReady = (
    settlement.state === 'finance_requested' &&
    transferProofs.length > 0
  )
  const emailStatus = settlement.email_delivery_status || 'not_sent'
  const emailInFlight = [ 'queued', 'submitted', 'processed', 'deferred' ].includes(emailStatus)
  const emailFailed = settlement.state === 'email_failed' || emailStatus === 'failed'
  const emailDelivered = emailStatus === 'delivered'

  return (
    <>
      <TitlePage
        mainTitle={ moduleTitle }
        subTitle={ teamLabel || 'Detail Settlement' }
      />

      { settlement.swap_note && <Alert color='info'><strong>Note: </strong>{settlement.swap_note}<div>Settlement pembelian dari consignor melalui swap; belum ada Deal penjualan.</div></Alert> }
      { settlement.can_view_buying_price === false && <Alert color='info'>Anda sedang membantu Settlement PIC { sellerMode ? settlement.sourced_by || 'belum ditentukan' : settlement.product_curator_pic || 'belum ditentukan' }. Buying price tersimpan disembunyikan. Nominal pembayaran tetap tersedia untuk pengajuan Finance. Kosongkan Final Buying Price untuk mempertahankan harga tersimpan, atau isi harga negosiasi baru lalu Save.</Alert> }

      { apiMessage && (
        <Alert color='warning'>
          { apiMessage }
        </Alert>
      ) }

      { successMessage && (
        <Alert color='success'>
          { successMessage }
        </Alert>
      ) }

      <div className='d-flex justify-content-between align-items-center flex-wrap mb-3'>
        <Button
          color='secondary'
          outline
          onClick={ backToList }
        >
          Back to List
        </Button>

        <div className='d-flex flex-column align-items-end' style={{ gap: '12px', padding: '4px 0' }}>
              { showFinanceGuidance && (
                <Button
                  color='primary'
                  className='mt-2 mt-md-0'
                  disabled={ !requestFinanceReady || !permissions.can_submit_finance_request || busyAction !== '' }
                  title={ requestFinanceReady ? 'Ajukan ke Finance' : uniqueFinanceBlockers.join('\n') }
                  onClick={ saveAllAndSubmit }
                >
                  Save & Ajukan Finance
                </Button>
              ) }
        <Badge
          color={ stateColor(settlement.state) }
          className='mt-2 mt-sm-0'
          style={{ fontSize: '0.8rem', padding: '0.55rem 0.8rem', whiteSpace: 'normal' }}
        >
          { stateLabel(settlement.state) }
        </Badge>
        </div>
      </div>
      { showFinanceGuidance && (
        <Alert color={ requestFinanceReady ? 'success' : 'info' }>
          <strong>{ requestFinanceReady ? 'Data lengkap — klik Save & Ajukan Finance.' : 'Lengkapi semua bagian, lalu klik Save & Ajukan Finance satu kali.' }</strong>
          <div>{ sellerMode ? 'Satu tombol menyimpan nominal dan bukti chat harga yang disepakati, pilihan rekening/email dan nominal pembayaran, lalu mengajukan ke Finance Approval. Negotiation Process wajib untuk Seller BKO; harga sourcing hanya referensi.' : 'Satu tombol menyimpan negosiasi/dokumen, pilihan rekening dan nominal pembayaran, lalu mengajukan ke Finance Approval.' }</div>
        </Alert>
      ) }
      { submissionStage === 'submitted' && (
        <Alert color='info'><strong>Permintaan pembayaran penuh sudah diajukan.</strong><div>Menunggu persetujuan / proses transfer Finance. Tidak perlu membuat draft atau mengklik Approval to Finance lagi. Status ini belum berarti uang sudah ditransfer.</div></Alert>
      ) }
      { completionNotice && <Alert color={ completionNotice.color }><strong>{ completionNotice.title }</strong><div>{ completionNotice.message }</div></Alert> }
      { showFinanceRequirements && (
        <Alert color='warning' style={{ backgroundColor: '#fff3cd', borderColor: '#ffe69c', color: '#664d03' }}>
          <strong>Save & Ajukan Finance belum aktif. Yang perlu dilengkapi:</strong>
          <ol className='mb-0 mt-2 pl-4'>
            { uniqueFinanceBlockers.map((message) => <li key={ message }>{ message }</li>) }
          </ol>
          <div className='mt-2'>
            <a href={ settlement.counterparty_user_detail_path || '#' } target='_blank' rel='noopener noreferrer'>Buka User Detail</a>
            {' '}untuk memperbaiki email, rekening dan dokumen. Setelah Save User/rekening, klik Refresh di Bank Account Process, pilih email/rekening, lalu klik Save & Ajukan Finance di atas.
          </div>
        </Alert>
      ) }
      <Alert color='info'>{ settlementStatusNote(settlement) }</Alert>
      { canNegotiate && !canFinanceTransfer && settlement.can_mark_transferred_manually && <Button className='mb-3' color='secondary' outline aria-expanded={ showManualTransfer } onClick={ () => setShowManualTransfer(!showManualTransfer) }>{ showManualTransfer ? 'Hide Manual TF Hint' : 'Show Manual TF Hint' }</Button> }
      { showManualTransfer && canNegotiate && !canFinanceTransfer && settlement.can_mark_transferred_manually && (
        <Alert color='warning'>
          <div className='mb-2'>Mark as transfer done manually hanya apabila Transfer / Settlement sudah dilakukan di luar sistem untuk Deal sebelum 15 Oktober 2026 pukul 00.00 WIB. Aksi ini mencatat penyelesaian manual tanpa mengirim uang atau email; draft pembayaran yang belum diajukan akan dibatalkan.</div>
          <Button color='warning' disabled={ busyAction !== '' } onClick={ markTransferredManually }>Mark as Transfer Done Manually</Button>
        </Alert>
      ) }
      { settlement.state === 'transferred_manually' && (
        <Alert color='success'>
          <strong>Transfer Done Manually — diselesaikan di luar sistem.</strong>
          <div>Dicatat oleh { settlement.manually_transferred_by_email || '-' } pada { formatDateTime(settlement.manually_transferred_at) }.</div>
          <div>{ settlement.manual_transfer_note }</div>
          <div>Total Transferred menampilkan transfer yang tercatat di sistem; pembayaran manual tidak ditambahkan ke ledger.</div>
        </Alert>
      ) }
      { canNegotiate && !canFinanceTransfer && submissionStage === 'prepare' && !showFinanceRequirements && !settlement.finance_deal_eligible && (
        <Alert color='warning'>{ settlement.finance_deal_requirement }</Alert>
      ) }

      { settlement.state === 'email_failed' && (
        <Alert color='danger'>
          <strong>Email bukti transfer gagal.</strong>{' '}
          Finance dapat mengoreksi email tujuan manual di bawah lalu mengirim ulang email bukti transfer.
          { settlement.email_failure_reason && (
            <div className='mt-2'>
              <strong>Kegagalan terakhir:</strong> { settlement.email_failure_reason }
            </div>
          ) }
        </Alert>
      ) }

      { !canFinanceTransfer && <PaidTransfersSummary settlement={ settlement } /> }
      <Modal isOpen={ !!priceError } toggle={ () => setPriceError('') }><ModalHeader toggle={ () => setPriceError('') }>Final Buying Price</ModalHeader><ModalBody>{ priceError }</ModalBody><ModalFooter><Button color='primary' onClick={ () => setPriceError('') }>OK</Button></ModalFooter></Modal>
      <Row className='align-items-stretch mb-3'>
        <Col lg={ 6 }>
          <Card className='h-100'>
            <CardHeader>
              <strong>Deal & Product</strong>
            </CardHeader>
            <CardBody>
              <InfoRow
                label='Settlement No'
                value={ settlement.settlement_number }
              />
              <InfoRow
                label='Deal Reference'
                value={ settlement.deal_id ? (
                  <a
                    href={ `/app/sales/deals/${settlement.deal_id}` }
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    { settlement.reference_number || '-' }
                  </a>
                ) : settlement.reference_number }
              />
              <InfoRow
                label='Deal Status'
                value={ settlement.deal_state || settlement.eligible_deal_state }
              />
              <InfoRow
                label='SKU'
                value={ settlement.sku }
                mono
              />
              <InfoRow
                label='Product'
                value={ settlement.inventory_id ? (
                  <a
                    href={ `/app/warehouse-team/inventory/${settlement.inventory_id}` }
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    { settlement.product_name || '-' }
                  </a>
                ) : settlement.product_name }
              />
              <InfoRow
                label='Internal Category'
                value={ settlement.internal_category }
              />
              { sellerMode && (
                <>
                  <InfoRow
                    label='Sourced By'
                    value={ settlement.sourced_by || '-' }
                  />


                </>
              ) }
              { !sellerMode && (
                <>
                  <InfoRow
                    label='Product Curator'
                    value={ settlement.product_curator_pic || '-' }
                  />


                </>
              ) }
            </CardBody>
          </Card>
        </Col>

        <Col lg={ 6 }>
          <Card className='h-100'>
            <CardHeader>
              <strong>Settlement</strong>
            </CardHeader>
            <CardBody>
              <InfoRow
                label='Buying Price'
                value={ settlement.can_view_buying_price === false ? '-' : `Rp ${formatMoney(settlement.current_product_buying_price)}` }
              />
              <InfoRow
                label='Final Buying Price'
                value={ settlement.can_view_buying_price === false ? '-' : `Rp ${formatMoney(settlement.settlement_buying_price)}` }
              />
              <InfoRow label='Selling Price' value={ `Rp ${formatMoney(settlement.selling_price)}` } />
              { settlement.payment_ledger && <>
                { !sellerMode && <InfoRow label='Email Status (per paid transfer)' value={ settlementEmailLabel(settlement) } /> }
                <InfoRow label='Total Transferred' value={ `Rp ${formatMoney(settlement.total_paid_idr)}` } />
                <InfoRow label='Remaining Payment' value={ settlement.can_view_buying_price === false ? '-' : `Rp ${formatMoney(settlement.remaining_payment_idr)}` } />
              </> }
              <InfoRow
                label={ 'Negotiated By' }
                value={ settlement.negotiated_by_email }
              />
              <InfoRow
                label={ 'Negotiated At' }
                value={ formatDateTime(settlement.negotiated_at) }
              />
              <InfoRow
                label={ useBusinessLabels ? 'Finance Approval' : 'CF Status' }
                value={ financeConfirmationLabel(financeConfirmationStatus) }
              />
              <InfoRow
                label={ useBusinessLabels ? 'Approval Requested By' : 'CF Requested By' }
                value={ settlement.finance_confirmation_requested_by_email }
              />
              <InfoRow
                label={ useBusinessLabels ? 'Approval Requested At' : 'CF Requested At' }
                value={ formatDateTime(settlement.finance_confirmation_requested_at) }
              />
              <InfoRow
                label={ useBusinessLabels ? 'Approval / Review By' : 'CF / Review By' }
                value={ settlement.finance_confirmation_reviewed_by_email }
              />
              <InfoRow
                label={ useBusinessLabels ? 'Approval / Review At' : 'CF / Review At' }
                value={ formatDateTime(settlement.finance_confirmation_reviewed_at) }
              />
              <InfoRow
                label={ useBusinessLabels ? 'Approval / Correction Note' : 'CF / Correction Note' }
                value={ settlement.finance_confirmation_note }
              />
              <InfoRow
                label='Submitted to Finance By'
                value={ settlement.finance_requested_by_email }
              />
              <InfoRow
                label='Submitted to Finance At'
                value={ formatDateTime(settlement.finance_requested_at) }
              />
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row className='align-items-stretch mb-3'>
        <Col lg={ 12 }>
          <Card className='h-100'>
            <CardHeader>
              <strong>{ `${counterpartyLabel} & Bank` }</strong>
            </CardHeader>
            <CardBody>
              { !transferDetail && permissions.can_connect_user && <SettlementConnectUser settlement={ settlement } onConnected={ loadDetail } /> }
              <InfoRow
                label='Name'
                value={ settlement.counterparty_user_detail_path
                  ? <a href={ settlement.counterparty_user_detail_path } target='_blank' rel='noopener noreferrer'>{ settlement.counterparty_name || settlement.consignor_name }</a>
                  : settlement.counterparty_name || settlement.consignor_name }
              />
              { !sellerMode && !canFinanceTransfer && <InfoRow
                label='Database Reference Email'
                value={ settlement.counterparty_reference_email || '-' }
              /> }
              <InfoRow
                label='Phone'
                value={ settlement.consignor_phone }
              />
              <InfoRow
                label='Bank'
                value={ bankName }
              />
              <InfoRow
                label='Account Holder'
                value={ bankAccountHolderName }
              />
              <InfoRow
                label='Account Number'
                value={ bankAccountNumber }
                mono
              />
              <InfoRow
                label='Branch'
                value={ bankBranch }
              />
              { settlement.bank_account_detail && <InfoRow label='Bank Account Detail' value={ settlement.bank_account_detail } /> }
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>


        { !settlement.payment_ledger && (
        <Col lg={ 6 }>
          <Card className='mb-3'>
            <CardHeader>
              <strong>{ sellerMode && !canFinanceTransfer ? 'Status Email & Inventory' : 'Status Email & Transfer' }</strong>
            </CardHeader>
            <CardBody>
              <InfoRow
                label='Transferred By'
                value={ settlement.transferred_by_email }
              />
              <InfoRow
                label='Transferred At'
                value={ formatDateTime(settlement.transferred_at) }
              />
              <InfoRow
                label={ `${counterpartyLabel} Email (To)` }
                value={ settlement.email_recipient || '-' }
              />
              <Row className='py-2 border-bottom'>
                <Col md={ 5 } className='text-muted'>Email Status</Col>
                <Col md={ 7 }>
                  <Badge color={ emailStatusColor(emailStatus) }>
                    { EMAIL_STATUS_LABELS[emailStatus] || emailStatus }
                  </Badge>
                </Col>
              </Row>
              <InfoRow
                label='Email Delivered At'
                value={ formatDateTime(settlement.email_sent_at) }
              />
              <Alert color='secondary' className='mt-3 mb-0'>
                Status email settlement mengikuti alamat <strong>To</strong> yang sudah diverifikasi Product Curator.
              </Alert>
            </CardBody>
          </Card>
        </Col>
        ) }
      </Row>

      { financeConfirmationStatus === 'rejected' && settlement.finance_confirmation_note && (
        <Alert color='warning'>
          <strong>Perlu koreksi / negosiasi ulang:</strong> { settlement.finance_confirmation_note }
        </Alert>
      ) }

      { canNegotiate && settlement.state !== 'transferred_manually' && (
        <Card className='mb-3'>
          <CardHeader>
            <div className='d-flex justify-content-between align-items-center flex-wrap'>
              <strong>1. Negotiation Process</strong>
            </div>
          </CardHeader>

          <CardBody>
            { lastSuccessAction === 'save-negotiation' && successMessage && <Alert color='success'>{ successMessage }</Alert> }
            { permissions.can_negotiate ? (
              <>
                <Row form>

                  <Col md={ 4 }>
                    <FormGroup>
                      <Label>Final Buying Price <span className='text-danger'>*</span></Label>
                      <Input
                        type='text'
                        inputMode='numeric'
                        autoComplete='off'
                        value={ formatMoneyInput(settlementBuyingPrice) }
                        disabled={ settlement.final_price_locked || busyAction !== '' }
                        onChange={ (event) => {
                          setSettlementBuyingPrice(
                            sanitizeMoneyInput(event.target.value)
                          )
                        } }
                        placeholder='Contoh: 700.000.000'
                      />
                      <small className='text-muted'>
                        { settlement.can_view_buying_price === false ? 'Harga tersimpan disembunyikan. Kosongkan untuk mempertahankan harga, atau isi harga negosiasi baru; batas harga diperiksa saat Save.' : `Maksimum: Rp ${formatMoney(settlement.current_product_buying_price)}.` } Bukti negosiasi wajib di-upload.
                      </small>
                    </FormGroup>
                  </Col>

                  <Col md={ 4 }>
                    <FormGroup>
                      <Label>Negotiation / Internal Notes</Label>
                      <Input
                        type='textarea'
                        rows={ 3 }
                        value={ notes }
                        disabled={ busyAction !== '' }
                        onChange={ (event) => setNotes(event.target.value) }
                        placeholder='Catatan tambahan untuk settlement ini (opsional)'
                      />
                    </FormGroup>
                  </Col>
                </Row>

              </>
            ) : (
              <Alert color='secondary'>
                Edit harga negosiasi dikunci untuk status { stateLabel(settlement.state) }.
              </Alert>
            ) }

            <Row>
              <Col md={ 7 }>
                <ProofList
                  title='Negotiation Proof'
                  onDelete={ settlement.can_delete_negotiation_proofs ? removeNegotiationProof : undefined }
                  deleting={ busyAction !== '' }
                  files={ negotiationProofs }
                  emptyText='Upload screenshot/PDF sebagai bukti harga yang sudah disepakati sebelum diajukan ke Finance.'
                />
              </Col>

              <Col md={ 5 }>
                { permissions.can_upload_negotiation_proofs ? (
                  <FormGroup>
                    <Label>Upload Negotiation Proof <span className='text-danger'>*</span></Label>
                    <Input
                      key={ negotiationInputKey }
                      disabled={ busyAction !== '' }
                      type='file'
                      multiple
                      accept='.jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf'
                      onChange={ chooseNegotiationFiles }
                    />
                    { negotiationFiles.map((file, index) => <div key={ index } className='mt-2'>{file.name} <Button type='button' color='danger' outline size='sm' disabled={ busyAction !== '' } onClick={() => { if (window.confirm(`Remove ${file.name} from upload selection?`)) setNegotiationFiles((files) => files.filter((_, i) => i !== index)) }}>Delete</Button></div>) }
                    <small className='text-muted d-block mb-2'>
                      Maksimal 5 file, maksimal 5 MB per file. Format: JPG, PNG, WEBP, atau PDF.
                    </small>
                  </FormGroup>
                ) : (
                  <Alert color='secondary'>
                    Upload bukti negosiasi dikunci pada tahap workflow ini.
                  </Alert>
                ) }
              </Col>
            </Row>

            <hr />

            <div className='text-muted small'>
              { sellerMode ? 'Approval to Finance aktif sejak Deal Sourced, Negotiation Process (nominal positif dan upload bukti chat deal harga), harga final, email yang dipilih (termasuk email placeholder), Bank Account Confirmation, bukti chat seller, dan draft pembayaran lengkap. Jika nama rekening berbeda dari Seller Detail, KTP seller dan KTP pemilik rekening juga wajib.' : 'Lengkapi harga final, bukti negosiasi, email, rekening dan nominal pembayaran. Klik Save & Ajukan Finance satu kali. Deal harus Completed; BKA/BKV hasil swap yang eligible dapat diajukan tanpa Deal.' }
            </div>

            { !requestFinanceReady && (
              <Alert color='warning' className='mt-3 mb-0'>
                { sellerMode ? 'Syarat wajib untuk upload KTP / bukti chat dengan seller kevalidan nomor rekening: pilih Seller Detail dan rekening di User Detail, upload bukti chat seller, lalu Save dan Refresh di sini. Jika nama rekening berbeda, upload juga KTP seller dan KTP pemilik rekening. Lengkapi Negotiation Process: nominal nego dan upload bukti chat deal harga. Pastikan Deal Sourced atau tahap aktif berikutnya, harga final dan draft pembayaran lengkap.' : 'Lengkapi Negosiasi, Email Tujuan, Bank Account Confirmation, dan bukti verifikasi identitas jika nama rekening berbeda sebelum Approval to Finance.' }
              </Alert>
            ) }
          </CardBody>
        </Card>
      ) }

      { canNegotiate && settlement.state !== 'transferred_manually' && (
        <Card className='mb-3'>
          <CardHeader className='d-flex justify-content-between align-items-center'><strong>{ '2. Bank Account Process' }</strong></CardHeader>
          <CardBody>
            <Alert color='info'>
              { sellerMode ? 'Pilih Seller Detail, tambah rekening dan bukti chat seller di ' : 'Tambah email, rekening dan bukti chat konfirmasi rekening di ' }
              <a href={ settlement.counterparty_user_detail_path || '#' } target='_blank' rel='noopener noreferrer'>User Detail</a>.
              {' '}Setelah disimpan, klik Refresh lalu pilih email dan rekening. { sellerMode ? 'Email placeholder boleh digunakan; bukti chat seller wajib. Jika nama rekening berbeda, upload KTP seller dan KTP pemilik rekening.' : 'Finance memeriksa dokumen saat Approval Transfer.' }
            </Alert>
            { lastSuccessAction === 'confirm-bank-account' && successMessage && <Alert color='success'>{ successMessage }</Alert> }
            <Button size='sm' className='mb-3' onClick={ loadDetail } disabled={ busyAction !== '' }>Refresh</Button>
            <Row form>
              <Col md={ 6 }>
                <Label>Registered Email</Label>
                <Input type='select' value={ emailRecipient } onChange={ (event) => setEmailRecipient(event.target.value) } disabled={ !permissions.can_negotiate || busyAction !== '' }>
                  <option value=''>Pilih email</option>
                  { counterpartyEmailOptions.map((email) => <option key={ email } value={ email }>{ email }</option>) }
                </Input>
              </Col>
              <Col md={ 6 }>
                <Label>Registered Bank Account</Label>
                <Input type='select' value={ selectedBankOptionKey } onChange={ selectBankAccountOption } disabled={ !permissions.can_negotiate || busyAction !== '' }>
                  <option value=''>Pilih rekening</option>
                  { bankAccountOptions.map((account) => (
                    <option key={ account.option_key } value={ account.option_key }>
                      { `${account.bank_name} — ${account.holder_name} — ${account.account_number} — ${account.branch || '-'}` }
                    </option>
                  )) }
                </Input>
              </Col>
            </Row>
          </CardBody>
        </Card>
      ) }



      { settlement.payment_ledger && <SettlementPaymentLedger settlement={ settlement } canNegotiate={ canNegotiate } canFinanceTransfer={ canFinanceTransfer } onSaved={ () => loadDetail(true) } onAmountChange={ canNegotiate && !canFinanceTransfer ? setPaymentAmount : undefined } externalBusy={ busyAction !== '' } /> }
      { !sellerMode && !canFinanceTransfer && <SettlementEmailDeliveryReconciliation settlement={ settlement } busy={ busyAction !== '' }
        onConfirm={ (paymentId, payload) => runWorkflow('mark-email-delivered', () => ConsignorSettlementsApiV2.markEmailDelivered(settlement.id, paymentId, payload), 'Status email transfer berhasil dikonfirmasi. Bukti dan admin dicatat di Settlement Movement.') } /> }


      { !settlement.payment_ledger && canNegotiate && (
        <Card className='mb-3'>
          <CardHeader>
            <strong>3. Transfer Slip</strong>
          </CardHeader>
          <CardBody>
            <ProofList
              title='Finance Transfer Proof'
              files={ transferProofs }
              emptyText='Belum ada bukti transfer. Bagian ini view only untuk Product Curator.'
            />
          </CardBody>
        </Card>
      ) }

      { canFinanceTransfer ? (
        <Card className='mb-3'>
          <CardHeader className='d-flex justify-content-between align-items-center'>
            <strong>Finance Approval</strong>
            { permissions.can_cancel_approved_finance && (
              <Button color='danger' outline size='sm' disabled={ busyAction !== '' } onClick={ cancelApprovedFinance }>
                Cancel Approval
              </Button>
            ) }
          </CardHeader>
          <CardBody>
            <Alert color={ financeConfirmationStatus === 'confirmed' ? 'success' : financeConfirmationStatus === 'rejected' ? 'warning' : 'info' }>
              <strong>Status:</strong>{' '}
              { financeConfirmationLabel(financeConfirmationStatus) }
              { financeConfirmationStatus === 'pending' && (
                <div className='mt-1'>{ useBusinessLabels ? 'Menunggu Finance approver sesuai Internal Category.' : 'Menunggu Mifta meng-CF nominal hasil negosiasi. Owner/Admin dapat membantu sebagai superuser.' }</div>
              ) }
            </Alert>

            { permissions.can_cancel_approved_finance && <Alert color='warning'>Belum ada transfer tercatat. Gunakan Cancel Approval jika harga atau detail pembayaran berubah sebelum transfer. Alasan wajib diisi; BT/PC perlu mengajukan ulang.</Alert> }
            { financeConfirmationStatus === 'pending' && !settlement.finance_deal_eligible && <Alert color='warning'>{ settlement.finance_deal_requirement }</Alert> }
            { successMessage && <Alert color='success'>{ successMessage }</Alert> }
            { financeConfirmationStatus === 'pending' && permissions.can_confirm_finance && (
              <>
                <FormGroup>
                  <Label>{ useBusinessLabels ? 'Catatan Approval / Instruksi Koreksi' : 'Catatan CF / Instruksi Koreksi' }</Label>
                  <Input
                    type='textarea'
                    rows={ 3 }
                    value={ financeConfirmationNote }
                    onChange={ (event) => setFinanceConfirmationNote(event.target.value) }
                    placeholder='Opsional saat CF. Wajib diisi jika dikembalikan untuk koreksi / negosiasi ulang.'
                  />
                </FormGroup>
                <div className='d-flex justify-content-end flex-wrap'>
                  <Button
                    color='danger'
                    outline
                    className='mr-2 mb-2'
                    disabled={ busyAction !== '' }
                    onClick={ rejectFinanceConfirmation }
                  >
                    Reject
                  </Button>
                  <Button
                    color='success'
                    className='mb-2'
                    disabled={ busyAction !== '' || !settlement.finance_deal_eligible }
                    onClick={ confirmFinance }
                  >
                    Approve
                  </Button>
                </div>
              </>
            ) }
          </CardBody>
        </Card>
      ) : null }

      { (canNegotiate || canFinanceTransfer) && (
      <Card className='mb-3'>
        <CardHeader className='d-flex justify-content-between align-items-center'>
          <strong>Supporting Documents</strong>
          { canNegotiate && !canFinanceTransfer && (
            <Button size='sm' type='button' aria-expanded={ supportingDocumentsOpen } aria-controls='settlement-supporting-documents' onClick={ () => setSupportingDocumentsOpen(!supportingDocumentsOpen) }>
              { supportingDocumentsOpen ? 'Hide Documents' : 'Show Documents' }
            </Button>
          ) }
        </CardHeader>
        { (!canNegotiate || canFinanceTransfer || supportingDocumentsOpen) && <CardBody id='settlement-supporting-documents'>
          { canFinanceTransfer && <Alert color='warning'>{ sellerMode ? 'Periksa bukti chat negosiasi harga seller pada Negotiation Process serta bukti chat seller yang menyetujui nomor dan nama rekening. Jika nama rekening berbeda dari Seller Detail, periksa juga KTP seller dan KTP pemilik rekening sebelum Approve atau transfer.' : 'Periksa bukti negosiasi (PC) dan bukti chat konfirmasi nomor serta nama rekening. KTP consignor dan KTP pemilik rekening hanya wajib jika nama berbeda.' }</Alert> }
          { (!sellerMode || canFinanceTransfer) && <ProofList title='Negotiation Process' files={ negotiationProofs } emptyText='Belum ada bukti negosiasi.' /> }
          <hr />
          <ProofList title='KTP / Identity Document' files={ identityVerificationProofs } emptyText={ settlement.identity_verification_required ? 'Belum ada KTP untuk rekening beda nama.' : 'Nama rekening sama: KTP tidak wajib.' } />
          <hr />
          <ProofList title='Bukti chat konfirmasi nomor dan nama rekening' files={ settlement.identity_chat_proofs || [] } emptyText='Belum ada bukti chat konfirmasi rekening.' /><hr />
          <ProofList title='Verifikasi Nama Rekening' files={ settlement.bank_verification_proofs || [] } emptyText='Tidak ada bukti rekening beda nama.' />
        </CardBody> }
      </Card>
      ) }
      { settlement.payment_ledger && <SettlementMovement settlement={ settlement } showEmail={ !sellerMode && !canFinanceTransfer } /> }

      { !settlement.payment_ledger && canFinanceTransfer && (
        <Card className='mb-3'>
          <CardHeader>
            <strong>Finance — Transfer Process</strong>
          </CardHeader>

          <CardBody>
            { [ 'ready_for_negotiation', 'negotiated', 'waiting_finance_confirmation' ].includes(settlement.state) ? (
              <Alert color='info'>
                Menunggu Approval Finance sebelum Transfer Process dapat dimulai.
              </Alert>
            ) : null }

            <Row>
              <Col md={ 7 }>
                <ProofList
                  title='Transfer Proof'
                  files={ transferProofs }
                  emptyText='No Finance transfer proof uploaded.'
                />
              </Col>

              <Col md={ 5 }>
                { permissions.can_upload_transfer_proofs ? (
                  <FormGroup>
                    <Label>Upload Transfer Proof</Label>
                    <Input
                      key={ transferInputKey }
                      type='file'
                      multiple
                      accept='.jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf'
                      onChange={ (event) => setTransferFiles(Array.from(event.target.files || [])) }
                    />
                    <small className='text-muted d-block mb-2'>
                      Bisa upload beberapa file. Semua bukti transfer akan tampil di Consignor Settlement dan dilampirkan pada email yang dikirim Product Curator.
                    </small>
                    <Button
                      color='primary'
                      outline
                      disabled={ transferFiles.length === 0 || busyAction !== '' }
                      onClick={ uploadTransferProofs }
                    >
                      Upload Selected Proof
                    </Button>
                  </FormGroup>
                ) : (
                  <Alert color='secondary'>
                    Upload bukti transfer belum tersedia pada tahap workflow ini.
                  </Alert>
                ) }
              </Col>
            </Row>

            { settlement.state === 'finance_requested' && (
              <div className='d-flex justify-content-end mt-3'>
                <Button
                  color='success'
                  disabled={ !markTransferredReady || !permissions.can_mark_transferred || busyAction !== '' }
                  onClick={ markTransferred }
                >
                  Mark as Transferred
                </Button>
              </div>
            ) }

            { settlement.state === 'finance_requested' && !markTransferredReady && (
              <Alert color='warning' className='mt-3 mb-0'>
                Upload minimal satu bukti transfer sebelum ditandai Sudah Ditransfer.
              </Alert>
            ) }
          </CardBody>
        </Card>
      ) }

      { !settlement.payment_ledger && !sellerMode && !canFinanceTransfer && [ 'finance_requested', 'transferred', 'email_failed', 'email_sent' ].includes(settlement.state) && (
        <Card className='mb-3'>
          <CardHeader>
            <strong>4. Product Curator — Transfer Email</strong>
          </CardHeader>
          <CardBody>
            { settlement.email_test_mode && (
              <Alert color='warning'>
                <strong>Mode pengalihan email non-production aktif.</strong>
                <div>
                  Intended consignor email: { settlement.email_recipient || 'belum diisi' }
                </div>
                <div>
                  Email yang benar-benar menerima: { settlement.email_test_recipient || '-' }
                </div>
                <div className='mt-1'>
                  Di staging/development/test, email tidak akan dikirim ke consignor/seller yang sebenarnya.
                </div>
              </Alert>
            ) }

            {!emailDelivered && (
              <Card className='mb-3 border'>
                <CardBody>
                  <FormGroup className='mb-2'>
                    <Label>Transfer Recipient Email</Label>
                    <Input
                      type='email'
                      value={ emailRecipient }
                      onChange={ (event) => setEmailRecipient(event.target.value) }
                      placeholder='email consignor yang valid'
                      disabled={ !permissions.can_update_email_recipient || busyAction !== '' }
                    />
                    <small className='text-muted d-block mt-1'>
                      Email referensi database: { settlement.counterparty_reference_email || '-' }. Gunakan alamat yang sudah diverifikasi benar. Jika bounce, Product Curator dapat koreksi di sini lalu kirim ulang.
                    </small>
                  </FormGroup>
                  <Button
                    color='secondary'
                    outline
                    size='sm'
                    disabled={ !permissions.can_update_email_recipient || !emailRecipient.trim() || busyAction !== '' }
                    onClick={ saveEmailRecipient }
                  >
                    Save Recipient Email
                  </Button>
                </CardBody>
              </Card>
            ) }

            <Alert color={ emailDelivered ? 'success' : emailFailed ? 'danger' : 'info' }>
              <div>
                <strong>Status:</strong>{' '}
                { EMAIL_STATUS_LABELS[emailStatus] || emailStatus }
              </div>
              <div>
                <strong>To:</strong> { settlement.email_recipient || 'Belum diisi Product Curator' }
              </div>
              <div>
                <strong>Jumlah Percobaan:</strong> { settlement.email_send_attempts || 0 }
              </div>
            </Alert>

            { settlement.email_failure_reason && (
              <Alert color='danger'>
                <strong>Kegagalan terakhir:</strong> { settlement.email_failure_reason }
                <div className='mt-2'>
                  Jika email tujuan salah/bounce, koreksi Email Tujuan Transfer di atas, simpan, lalu gunakan Kirim Ulang Email.
                </div>
              </Alert>
            ) }

            { emailInFlight && (
              <Alert color='warning'>
                SendGrid belum mengonfirmasi pengiriman akhir. Jangan kirim ulang selama percobaan masih dalam antrean, diproses, atau ditunda.
              </Alert>
            ) }

            { emailDelivered ? (
              <Alert color='success'>
                SendGrid sudah mengonfirmasi email terkirim ke alamat To. Status settlement menjadi Email Delivered.
              </Alert>
            ) : (
              <div className='d-flex justify-content-end'>
                <Button
                  color='primary'
                  disabled={ !permissions.can_send_email || busyAction !== '' || emailInFlight }
                  onClick={ sendTransferEmail }
                >
                  { Number(settlement.email_send_attempts || 0) > 0
                    ? 'Resend Transfer Proof Email'
                    : 'Send Transfer Proof Email' }
                </Button>
              </div>
            ) }

          </CardBody>
        </Card>
      ) }



    </>
  )
}

ConsignorSettlementDetailPage.propTypes = propTypes
ConsignorSettlementDetailPage.defaultProps = defaultProps

export default ConsignorSettlementDetailPage
